import React, { useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Snackbar from '@material-ui/core/Snackbar';
import Grid from '@material-ui/core/Grid';
import PrintIcon from '@material-ui/icons/Print';
import PaymentIcon from '@material-ui/icons/Payment';
import WarningIcon from '@material-ui/icons/Warning';
import { makeStyles } from '@material-ui/core/styles';
import { DoctorTypeBadge } from '../shared';
import { FEE_MODEL, DOCTOR_TYPE } from '../shared/constants';

const FEE_MODEL_LABELS = {
  [FEE_MODEL.PERCENTAGE_SPLIT]: 'Percentage Split',
  [FEE_MODEL.FLAT_RATE]: 'Flat Rate',
  [FEE_MODEL.DIRECT_BILLING]: 'Direct Billing',
};

const REMITTANCE_STATUS_MAP = {
  pending: { label: 'Pending', color: '#FF9800', bg: '#FFF8E1' },
  processed: { label: 'Processed', color: '#2196F3', bg: '#E3F2FD' },
  paid: { label: 'Paid', color: '#4CAF50', bg: '#E8F5E9' },
};


const useStyles = makeStyles((theme) => ({
  root: {
    margin: 22,
    padding: 20,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  title: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#333',
  },
  sectionTitle: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#333',
    marginTop: 24,
    marginBottom: 12,
  },
  alertBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#FFF3E0',
    border: '1px solid #FFCC80',
    borderRadius: 6,
    padding: '12px 16px',
    marginBottom: 20,
  },
  alertIcon: {
    color: '#E65100',
    fontSize: '1.3rem',
  },
  alertText: {
    fontSize: '0.85rem',
    color: '#BF360C',
    fontWeight: 500,
  },
  infoCard: {
    padding: '16px 20px',
    marginBottom: 16,
    borderRadius: 8,
    border: '1px solid #E0E0E0',
  },
  doctorHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  doctorName: {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#222',
  },
  detailRow: {
    display: 'flex',
    padding: '6px 0',
  },
  detailLabel: {
    width: 200,
    fontSize: '0.85rem',
    color: '#777',
    fontWeight: 500,
    flexShrink: 0,
  },
  detailValue: {
    fontSize: '0.85rem',
    color: '#333',
    flex: 1,
  },
  calcTable: {
    marginBottom: 16,
  },
  calcCell: {
    fontSize: '0.9rem',
    color: '#333',
    padding: '10px 16px',
    borderBottom: '1px solid #F0F0F0',
  },
  calcLabel: {
    fontSize: '0.9rem',
    color: '#555',
    fontWeight: 500,
    padding: '10px 16px',
    borderBottom: '1px solid #F0F0F0',
  },
  calcAmount: {
    fontSize: '0.9rem',
    color: '#333',
    fontFamily: 'monospace',
    fontWeight: 500,
    padding: '10px 16px',
    borderBottom: '1px solid #F0F0F0',
    textAlign: 'right',
  },
  netPayableRow: {
    backgroundColor: '#E8F5E9',
  },
  netPayableLabel: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#2E7D32',
    padding: '12px 16px',
  },
  netPayableAmount: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#2E7D32',
    fontFamily: 'monospace',
    padding: '12px 16px',
    textAlign: 'right',
  },
  remittanceSection: {
    padding: '16px 20px',
    borderRadius: 8,
    border: '1px solid #E0E0E0',
    marginBottom: 16,
  },
  remittanceHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  statusChip: {
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  actions: {
    display: 'flex',
    gap: 12,
    marginTop: 20,
  },
  actionBtn: {
    textTransform: 'none',
    fontWeight: 500,
  },
}));

const DoctorFeeSplit = ({ surgeryId }) => {
  const classes = useStyles();
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const doctor = null;
  const agreement = null;
  const calc = null;
  const remittance = null;
  const remittanceInfo = REMITTANCE_STATUS_MAP.pending;

  const isExternal = false;

  const handleProcessPayment = () => {
    setSnackbar({ open: true, message: 'Processing doctor payment...' });
  };

  const handlePrint = () => {
    setSnackbar({ open: true, message: 'Preparing fee statement for printing...' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '' });
  };

  if (!doctor) {
    return (
      <Paper className={classes.root} elevation={1}>
        <div className={classes.header}>
          <Typography className={classes.title}>
            Doctor Fee Split &mdash; Surgery #{surgeryId}
          </Typography>
        </div>
        <Typography variant="body2" style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', padding: 32 }}>
          No doctor fee agreement configured for this surgery.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root} elevation={1}>
      {/* Header */}
      <div className={classes.header}>
        <Typography className={classes.title}>
          Doctor Fee Split &mdash; Surgery #{surgeryId}
        </Typography>
      </div>

      {/* External Doctor Alert */}
      {isExternal && (
        <div className={classes.alertBanner}>
          <WarningIcon className={classes.alertIcon} />
          <Typography className={classes.alertText}>
            External doctor &mdash; verify agreement terms before processing
          </Typography>
        </div>
      )}

      {/* Doctor Info Section */}
      <Typography className={classes.sectionTitle}>Doctor Information</Typography>
      <div className={classes.infoCard}>
        <div className={classes.doctorHeader}>
          <Typography className={classes.doctorName}>{doctor.name}</Typography>
          <DoctorTypeBadge doctorType={doctor.type} />
        </div>
        <div className={classes.detailRow}>
          <Typography className={classes.detailLabel}>Specialty</Typography>
          <Typography className={classes.detailValue}>{doctor.specialty}</Typography>
        </div>
        <div className={classes.detailRow}>
          <Typography className={classes.detailLabel}>License Number</Typography>
          <Typography className={classes.detailValue}>{doctor.license_number}</Typography>
        </div>
      </div>

      {/* Fee Agreement Summary */}
      <Typography className={classes.sectionTitle}>Fee Agreement Summary</Typography>
      <div className={classes.infoCard}>
        <div className={classes.detailRow}>
          <Typography className={classes.detailLabel}>Fee Model</Typography>
          <Typography className={classes.detailValue}>
            {FEE_MODEL_LABELS[agreement.model] || agreement.model}
          </Typography>
        </div>
        {agreement.model === FEE_MODEL.PERCENTAGE_SPLIT && (
          <div className={classes.detailRow}>
            <Typography className={classes.detailLabel}>Split Ratio</Typography>
            <Typography className={classes.detailValue}>
              Doctor {agreement.doctor_percentage}% / Hospital {agreement.hospital_percentage}%
            </Typography>
          </div>
        )}
        {agreement.model === FEE_MODEL.FLAT_RATE && (
          <div className={classes.detailRow}>
            <Typography className={classes.detailLabel}>Flat Rate</Typography>
            <Typography className={classes.detailValue}>Per procedure rate as agreed</Typography>
          </div>
        )}
        <div className={classes.detailRow}>
          <Typography className={classes.detailLabel}>Agreement Reference</Typography>
          <Typography className={classes.detailValue}>{agreement.agreement_ref}</Typography>
        </div>
        <div className={classes.detailRow}>
          <Typography className={classes.detailLabel}>Agreement Period</Typography>
          <Typography className={classes.detailValue}>
            {agreement.period_start} &mdash; {agreement.period_end}
          </Typography>
        </div>
      </div>

      {/* Fee Calculation Table */}
      <Typography className={classes.sectionTitle}>Fee Calculation</Typography>
      <Table className={classes.calcTable} size="small">
        <TableBody>
          <TableRow>
            <TableCell className={classes.calcLabel}>Total Surgeon Fee Collected</TableCell>
            <TableCell className={classes.calcAmount}>
              UGX {calc.total_surgeon_fee.toLocaleString()}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.calcLabel}>
              Doctor's Share ({agreement.doctor_percentage}%)
            </TableCell>
            <TableCell className={classes.calcAmount}>
              UGX {calc.doctor_share.toLocaleString()}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.calcLabel}>
              Hospital's Share ({agreement.hospital_percentage}%)
            </TableCell>
            <TableCell className={classes.calcAmount}>
              UGX {calc.hospital_share.toLocaleString()}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.calcLabel}>
              Tax Withheld ({calc.tax_rate}%)
            </TableCell>
            <TableCell className={classes.calcAmount} style={{ color: '#C62828' }}>
              - UGX {calc.tax_withheld.toLocaleString()}
            </TableCell>
          </TableRow>
          <TableRow className={classes.netPayableRow}>
            <TableCell className={classes.netPayableLabel}>Net Payable to Doctor</TableCell>
            <TableCell className={classes.netPayableAmount}>
              UGX {calc.net_payable.toLocaleString()}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {/* Remittance Status */}
      <Typography className={classes.sectionTitle}>Remittance Status</Typography>
      <div className={classes.remittanceSection}>
        <div className={classes.remittanceHeader}>
          <Typography style={{ fontSize: '0.9rem', fontWeight: 500, color: '#555' }}>
            Payment Status
          </Typography>
          <Chip
            label={remittanceInfo.label}
            className={classes.statusChip}
            style={{ backgroundColor: remittanceInfo.bg, color: remittanceInfo.color }}
          />
        </div>
        {remittance.status === 'pending' && (
          <Typography style={{ fontSize: '0.85rem', color: '#E65100' }}>
            Payment has not yet been processed. Verify agreement terms and approve to proceed.
          </Typography>
        )}
        {remittance.status === 'processed' && (
          <div className={classes.detailRow}>
            <Typography className={classes.detailLabel}>Processed Date</Typography>
            <Typography className={classes.detailValue}>{remittance.processed_date}</Typography>
          </div>
        )}
        {remittance.status === 'paid' && (
          <>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Processed Date</Typography>
              <Typography className={classes.detailValue}>{remittance.processed_date}</Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Paid Date</Typography>
              <Typography className={classes.detailValue}>{remittance.paid_date}</Typography>
            </div>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PaymentIcon />}
          className={classes.actionBtn}
          onClick={handleProcessPayment}
        >
          Process Payment
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PrintIcon />}
          className={classes.actionBtn}
          onClick={handlePrint}
        >
          Print Fee Statement
        </Button>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default DoctorFeeSplit;
